import { Alert, CircularProgress } from "@mui/material";
import * as React from "react";

import {
  AggregateMetricsRequest,
  ChartDisplayType,
  NumericAggregation,
  Period,
} from "@/shared/domain/metrics";
import { useAggregateMetrics } from "@/shared/domain/metrics/hooks";
import { nsToDateLabel } from "@/shared/time/time";

import { fillMissingBuckets } from "./fillMissingBuckets";
import styles from "./MetricChart.module.css";
import { MetricChartView, type PlotDatum } from "./MetricChartView";

export interface MetricChartProps {
  metricName: string;
  orgId: string | undefined;
  period: Period;
  aggregation: NumericAggregation;
  /** Inclusive lower bound, Unix-epoch nanoseconds. */
  startTimeNs: number;
  /** Exclusive upper bound, Unix-epoch nanoseconds. */
  endTimeNs: number;
  /** Optional: restrict to specific device(s). */
  deviceIds?: string[];
  /** Optional: restrict to specific session(s). */
  sessionIds?: string[];
  /** Line (default) or bar rendering. */
  display?: ChartDisplayType;
  /** Fired when a real (non-placeholder) bucket is clicked. */
  onBucketClick?: (bucket: { start_time: bigint; end_time: bigint }) => void;
}

/**
 * Data wrapper for {@link MetricChartView}: fetches aggregate metrics, handles
 * loading/error states, gap-fills the series, and maps records to the
 * pre-formatted {@link PlotDatum}[] the pure view renders.
 */
export function MetricChart({
  metricName,
  orgId,
  period,
  aggregation,
  startTimeNs,
  endTimeNs,
  deviceIds,
  sessionIds,
  display = "line",
  onBucketClick,
}: MetricChartProps) {
  const request: AggregateMetricsRequest = {
    name: metricName,
    period,
    aggregation,
    time_filter: "end_time",
    start_time_ns: startTimeNs,
    end_time_ns: endTimeNs,
    include_device_ids: deviceIds,
    include_session_ids: sessionIds,
  };

  const { data, isLoading, error } = useAggregateMetrics(request, orgId);

  const series: PlotDatum[] = React.useMemo(() => {
    if (!data) {
      return [];
    }
    // Carry the bigint ns bounds through unchanged — only the display strings
    // need a (lossy, but date-resolution-irrelevant) `number` via nsToDateLabel.
    const records = data.records.map((r) => ({
      start_time: r.start_time,
      end_time: r.end_time,
      value: r.value,
    }));
    const filled = fillMissingBuckets(records);
    return filled.map((b) => ({
      start_time: b.start_time,
      end_time: b.end_time,
      value: "value" in b ? (b as { value: number }).value : undefined,
      label: nsToDateLabel(Number(b.start_time)),
      tooltipTitle: `${nsToDateLabel(Number(b.start_time))} - ${nsToDateLabel(Number(b.end_time))}`,
    }));
  }, [data]);

  if (isLoading) {
    return (
      <div className={styles.state}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load metric data: {error.message}
      </Alert>
    );
  }

  return (
    <MetricChartView
      series={series}
      metricName={metricName}
      // Every bucket carries the same definition-resolved unit, so the first
      // record is representative.
      unit={data?.records[0]?.unit ?? null}
      display={display}
      onBucketClick={onBucketClick}
    />
  );
}
